<template>
  <div class="min-h-screen bg-bochas-bg">
    <!-- Hero (estilo home: navbar transparente sobre el hero, fondo llega a top:0) -->
    <ImageBackgroundSection
      seed="noticias-hero"
      :image-url="heroImageUrl"
      :loading-image="heroImageLoading"
      alt="Novedades"
      :title="heroMedia?.title || 'Novedades'"
      :subtitle="
        heroMedia?.subtitle ||
        'Enterate de las novedades, logros y acontecimientos que impulsan a las bochas cordobesas'
      "
    />

    <!-- Filtros -->
    <section
      class="max-w-7xl mx-auto pt-16 px-6 sm:px-6 lg:px-0 lg:pt-24 lg:pb-0 sm:py-12"
    >
      <div class="section-header">
        <div class="section-header__icon">
          <IconFbochas name="newspaper" :size="32" color="#fff" />
        </div>
        <span class="section-header__label">Novedades</span>
      </div>
      <div class="flex flex-wrap gap-2 items-center">
        <button
          @click="setFilter(null)"
          :class="[
            'px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-colors',
            filterTag === null
              ? 'bg-bochas-rojo text-white'
              : 'bg-white/10 text-gray-300 hover:bg-white/20',
          ]"
        >
          Todas
        </button>
        <button
          v-for="tag in tags"
          :key="tag.id"
          @click="setFilter(tag.id)"
          :class="[
            'px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-colors',
            filterTag === tag.id
              ? 'bg-bochas-rojo text-white'
              : 'bg-white/10 text-gray-300 hover:bg-white/20',
          ]"
        >
          {{ tag.name }}
        </button>
      </div>
    </section>

    <!-- Loading -->
    <section
      v-if="loading"
      class="max-w-7xl mx-auto pt-16 px-6 sm:px-6 lg:px-0 lg:py-8 sm:py-12"
    >
      <div
        class="inline-block w-10 h-10 sm:w-12 sm:h-12 border-4 border-bochas-rojo border-t-transparent rounded-full animate-spin"
      ></div>
    </section>

    <!-- Empty -->
    <section
      v-else-if="noticias.length === 0"
      class="max-w-7xl mx-auto pt-16 px-6 sm:px-6 lg:px-0 lg:py-8 sm:py-12"
    >
      <p class="text-gray-400 text-base sm:text-lg">
        No hay noticias publicadas todavía.
      </p>
    </section>

    <!-- Listado -->
    <section
      v-else
      class="max-w-7xl mx-auto pt-6 px-6 pb-12 sm:px-6 lg:px-0 lg:pt-12 lg:pb-24 sm:py-12"
    >
      <div
        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 sm:gap-6"
      >
        <RevealOnScroll
          v-for="(noticia, index) in noticias"
          :key="noticia.id"
          as="article"
          :delay="(index % 3) * 100"
          class="card-fbochas overflow-hidden flex flex-col transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-bochas-rojo/30 active:scale-[0.98]"
        >
          <router-link
            :to="`/noticias/${noticia.id}`"
            class="flex flex-col h-full"
          >
            <!-- Imagen de portada con badge de categoría superpuesto -->
            <div class="relative">
              <div
                v-if="noticia.cover_image"
                class="aspect-video overflow-hidden bg-bochas-bg"
              >
                <img
                  :src="noticia.cover_image"
                  :alt="noticia.title"
                  class="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div
                v-else
                class="aspect-video bg-degradado-bochas flex items-center justify-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="w-12 h-12 sm:w-16 sm:h-16 text-white/30"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="1"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                  />
                </svg>
              </div>
              <span
                v-if="noticia.tag"
                class="absolute top-2 left-2 sm:top-3 sm:left-3 text-[10px] sm:text-xs font-semibold uppercase tracking-wide px-2 sm:px-3 py-1 rounded backdrop-blur-sm"
                :style="{
                  backgroundColor: (noticia.tag.color || '#A52121') + 'cc',
                  color: 'white',
                }"
              >
                {{ noticia.tag.name }}
              </span>
            </div>

            <!-- Contenido -->
            <div class="p-4 sm:p-5 flex-1 flex flex-col">
              <h2
                class="text-base sm:text-lg font-bold text-white mb-2 line-clamp-2"
              >
                {{ noticia.title }}
              </h2>
              <p class="text-xs sm:text-sm text-gray-300 line-clamp-3 flex-1">
                {{ noticia.description || textPreview(noticia.content) }}
              </p>
            </div>

            <!-- Footer con fecha + Leer más -->
            <div
              class="px-4 sm:px-5 py-3 border-t border-white/10 flex items-center justify-between text-xs sm:text-sm"
            >
              <time class="text-gray-400">
                {{ formatDate(noticia.published_at) }}
              </time>
              <span class="text-white font-semibold">Leer más →</span>
            </div>
          </router-link>
        </RevealOnScroll>
      </div>

      <!-- Paginación -->
      <Paginación
        class="mt-14"
        :current-page="currentPage"
        :total-items="totalItems"
        :per-page="perPage"
        @change="onPageChange"
      />
    </section>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useListingState } from "@/composables/useListingState";
import { listarNoticias } from "@/services/noticias/noticiasService";
import { listarTags } from "@/services/tags/tagsService";
import { formatDate } from "@/utils/date";
import IconFbochas from "@/components/Icons/IconFbochas.vue";
import Paginación from "@/components/Common/Paginacion.vue";
import ImageBackgroundSection from "@/components/Common/ImageBackgroundSection.vue";
import RevealOnScroll from "@/components/Animations/RevealOnScroll.vue";
import { useSiteMediaImageState } from "@/composables/useSiteMediaImage";

const {
  imageUrl: heroImageUrl,
  item: heroMedia,
  loading: heroImageLoading,
} = useSiteMediaImageState("noticias.hero");
const noticias = ref([]);
const tags = ref([]);
const loading = ref(true);
const { filterTag, currentPage } = useListingState("noticias", { filterTag: null, currentPage: 1 });

// Paginación: el backend ya soporta `limit` y `offset` en /news.
const perPage = 6;
const totalItems = ref(0);
const totalPages = computed(() =>
  Math.max(1, Math.ceil(totalItems.value / perPage)),
);

async function load() {
  loading.value = true;
  try {
    const data = await listarNoticias({
      limit: perPage,
      offset: (currentPage.value - 1) * perPage,
      tagId: filterTag.value,
      type: "NOTICIA",
    });
    noticias.value = data.items || [];
    totalItems.value = data.total || 0;
  } catch (_) {
    noticias.value = [];
    totalItems.value = 0;
  } finally {
    loading.value = false;
  }
}

function onPageChange(page) {
  currentPage.value = page;
  load();
  // El scrollToTop lo hace el componente Paginación
}

function setFilter(tagId) {
  filterTag.value = tagId;
  // Reset a página 1 al cambiar filtro
  currentPage.value = 1;
  load();
}

// Extrae texto plano de un HTML para preview rápido
function textPreview(html) {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  const txt = tmp.textContent || tmp.innerText || "";
  return txt.slice(0, 180) + (txt.length > 180 ? "..." : "");
}

onMounted(async () => {
  try {
    tags.value = await listarTags();
  } catch {
    tags.value = [];
  }
  load();
});
</script>
